import React, { useRef, useState } from "react";
import { CloudUpload, ZoomIn, ZoomOut } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { ButtonContained, ButtonOutlined, ButtonText } from "../../../components";
import S from "../styles/StylesWebcam";

interface UploadImageTabProps {
  facialFormat?: string;
  onConfirm: (imgSrc: { src: string; name: string }) => void;
  onBack: () => void;
}

function UploadImageTab({ facialFormat, onConfirm, onBack }: UploadImageTabProps) {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result as string);
      setPan({ x: 0, y: 0 });
      setZoom(1);
      setIsLoading(false);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const resetUpload = () => {
    setUploadedImage(null);
    setPan({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging) {
      setPan({ x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleConfirm = () => {
    if (!uploadedImage || !containerRef.current) return;
    setIsLoading(true);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const targetWidth = 640;
      const targetHeight = 480;
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) { setIsLoading(false); return; }

      const containerWidth = containerRef.current!.clientWidth;
      const containerHeight = containerRef.current!.clientHeight;
      const scaleX = targetWidth / containerWidth;
      const scaleY = targetHeight / containerHeight;

      const canvasAspect = targetWidth / targetHeight;
      const imgAspect = img.width / img.height;

      let displayWidth: number, displayHeight: number;
      if (imgAspect > canvasAspect) {
        displayWidth = targetWidth;
        displayHeight = targetWidth / imgAspect;
      } else {
        displayHeight = targetHeight;
        displayWidth = targetHeight * imgAspect;
      }

      const scaledWidth = displayWidth * zoom;
      const scaledHeight = displayHeight * zoom;
      const drawX = (targetWidth - scaledWidth) / 2 + pan.x * scaleX;
      const drawY = (targetHeight - scaledHeight) / 2 + pan.y * scaleY;

      ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);

      const src = canvas.toDataURL(facialFormat === "PNG" ? "image/png" : "image/jpeg", 0.95);
      onConfirm({ src, name: "uploaded-photo" });
    };
    img.src = uploadedImage;
  };

  return (
    <S.Container>
      <S.WebcamContent>
        {!uploadedImage ? (
          <S.UploadDropzone onClick={() => !isLoading && fileInputRef.current?.click()}>
            <input type="file" accept="image/*" hidden ref={fileInputRef} onChange={handleFileUpload} />
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <CloudUpload />
                Clique para selecionar uma imagem
              </>
            )}
          </S.UploadDropzone>
        ) : (
          <div
            ref={containerRef}
            style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={uploadedImage}
              alt="Upload"
              draggable={false}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                cursor: isDragging ? "grabbing" : "grab",
                width: "100%",
                height: "100%",
                objectFit: "contain",
                userSelect: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "40%",
                height: "70%",
                border: "3px solid #4CAF50",
                borderRadius: "50% / 50%",
                pointerEvents: "none",
                zIndex: 10,
              }}
            />
          </div>
        )}
      </S.WebcamContent>

      {uploadedImage && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 16px",
          width: "100%",
          boxSizing: "border-box",
        }}>
          <ZoomOut fontSize="small" style={{ color: "#888" }} />
          <input
            type="range"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            min={0.5}
            max={3}
            step={0.05}
            style={{ flex: 1 }}
          />
          <ZoomIn fontSize="small" style={{ color: "#888" }} />
        </div>
      )}

      <S.Actions>
        {uploadedImage && (
          <>
            <ButtonContained onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? "Processando..." : "Continuar"}
            </ButtonContained>
            <ButtonOutlined onClick={resetUpload}>Remover</ButtonOutlined>
          </>
        )}
        <ButtonText onClick={onBack}>Voltar</ButtonText>
      </S.Actions>
    </S.Container>
  );
}

export default UploadImageTab;
